import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { apiString } from '../../../utils/constants';

export interface TeamMessagesProps {
    teamId?: string;
}

const TeamMessages: React.FC<TeamMessagesProps> = ({ teamId }) => {
    const [notes, setNotes] = useState<any[]>([]);
    const [loadingNotes, setLoadingNotes] = useState<boolean>(false);
    const [noteContent, setNoteContent] = useState<string>('');
    const [addingNote, setAddingNote] = useState<boolean>(false);
    const [notesError, setNotesError] = useState<string>('');

    useEffect(() => {
        const fetchNotes = async () => {
            if (!teamId) return;
            setLoadingNotes(true);
            try {
                const resp = await axios.get(`${apiString}/careteamnotes/notes`, { params: { teamId } });
                if (resp.status === 200) setNotes(resp.data || []);
            } catch (err) {
                console.error('Error loading notes', err);
                setNotesError('Failed to load notes');
            } finally {
                setLoadingNotes(false);
            }
        };
        fetchNotes();
    }, [teamId]);

    const handleAddNote = async () => {
        if (!teamId) {
            setNotesError('No team selected');
            return;
        }
        if (!noteContent.trim()) return;
        setAddingNote(true);
        setNotesError('');
        try {
            const payload = { TeamId: teamId, Content: noteContent.trim() };
            const resp = await axios.post(`${apiString}/careteamnotes/notes`, payload);
            if (resp.status === 200 || resp.status === 201) {
                setNotes((prev) => [resp.data, ...prev]);
                setNoteContent('');
            } else {
                setNotesError('Failed to add note');
            }
        } catch (err: any) {
            const msg = err?.response?.data || err?.message || 'Failed to add note';
            setNotesError(typeof msg === 'string' ? msg : JSON.stringify(msg));
            console.debug('Add note error:', err);
        } finally {
            setAddingNote(false);
        }
    };

    return (
        <div>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
                Team Messages
            </Typography>
            <Paper elevation={2} sx={{ padding: 2, marginBottom: 2, background: '#f9f9f9' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', marginBottom: 2 }}>
                    <TextField
                        label="Add a note..."
                        multiline
                        minRows={2}
                        value={noteContent}
                        onChange={(e) => setNoteContent(e.target.value)}
                        fullWidth
                        variant="outlined"
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddNote}
                        disabled={addingNote || !noteContent.trim()}
                        sx={{ height: 48 }}
                    >
                        {addingNote ? <CircularProgress size={20} /> : 'Submit'}
                    </Button>
                </Box>
                {notesError && (
                    <Typography color="error" variant="body2">
                        {notesError}
                    </Typography>
                )}
                {loadingNotes ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80px' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        {notes.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                                No notes yet.
                            </Typography>
                        ) : (
                            notes.map((note, idx) => (
                                <Paper
                                    key={note.Id || idx}
                                    elevation={0}
                                    sx={{ padding: 2, marginBottom: 1, background: '#fff', borderLeft: '4px solid #1976d2' }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, marginRight: 1 }}>
                                            {note.AuthorName || 'Unknown'}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {note.Timestamp ? new Date(note.Timestamp).toLocaleString() : ''}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1">{note.Content}</Typography>
                                </Paper>
                            ))
                        )}
                    </Box>
                )}
            </Paper>
        </div>
    );
};

export default TeamMessages;
